import {
    Card,
    CardBody,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Pagination,
    PaginationItem,
    PaginationLink,
    Row,
    Table,
} from 'reactstrap';
import { DegreeTypes } from '../../common/Data';
import React, { useEffect, useState, useCallback, useMemo } from 'react';

import { Button } from '../../components';
import MultiSelect from 'react-multi-select-component';
import { ProgramStatus, States } from '../../common/Data';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import {
    AccreditedProgramDto,
    AuspiceDto,
    Institution,
    InstitutionTypeDto,
    ProfessionDto,
    ProfessionLevel,
} from '../../common/Types';
import { professionService } from '../../services/ProfessionAdministration';
import { institutionService } from '../../services/InstitutionService';
import { reportService } from '../../services/ReportService';
import moment from 'moment';
import { PagingDto } from '../../types';

const StyledMultiSelect = styled(MultiSelect)`
    width: 300px;

    & .dropdown-container {
        width: 100%;
        max-width: 100%;
    }

    & .dropdown-content {
        z-index: 3;
        border: none !important;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;

        /* Force all child elements to have minimal left padding */
        & > * {
            padding-left: 0.25rem !important;
        }
    }

    & .select-item {
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        padding-left: 0.5rem !important;
        padding-right: 0.5rem !important;

        /* Ensure selection background spans full width */
        &.selected {
            background-color: rgba(13, 110, 253, 0.1) !important;
            width: 100% !important;
            margin-left: 0 !important;
            padding-left: 0.5rem !important;
        }

        &:hover {
            background-color: rgba(0,0,0,0.05);
        }

        /* Force all children to stay in row */
        > * {
            display: inline-flex !important;
            align-items: center !important;
        }
    }

    /* Force label to be flex row */
    & .select-item label {
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        margin: 0;
        cursor: pointer;
    }

    /* Keep checkboxes inline with proper spacing */
    & .select-item input[type="checkbox"] {
        display: inline-block !important;
        margin-right: 0.5rem !important;
        vertical-align: middle !important;
        flex-shrink: 0;
    }

    /* Ensure text stays inline */
    & .select-item span,
    & .select-item label span {
        display: inline-block !important;
        vertical-align: middle !important;
    }
`;

// Type for multi-select options
interface SelectOption {
    label: string;
    value: string;
}

const AccreditedProgramsReportNew: React.FC = () => {
    // Core state
    const [total, setTotal] = useState<number>(0);
    const [accreditedPrograms, setAccreditedPrograms] = useState<AccreditedProgramDto[]>([]);

    // Filter states
    const [professionId, setProfessionId] = useState<number>(0);
    const [professionLevelId, setProfessionLevelId] = useState<number>(0);
    const [selectedStatus, setSelectedStatus] = useState<SelectOption[]>([]);
    const [institutionId, setInstitutionId] = useState<number>(0);
    const [institutionTypeId, setInstitutionTypeId] = useState<number>(0);
    const [selectedDegreeType, setSelectedDegreeType] = useState<SelectOption[]>([]);
    const [selectedAuspices, setSelectedAuspices] = useState<SelectOption[]>([]);
    const [city, setCity] = useState<string>('');
    const [selectedStates, setSelectedStates] = useState<SelectOption[]>([]);
    const [distance, setDistance] = useState<boolean>(false);

    // Data states
    const [institutions, setInstitutions] = useState<Institution[]>([]);
    const [auspices, setAuspices] = useState<AuspiceDto[]>([]);
    const [institutionTypes, setInstitutionTypes] = useState<InstitutionTypeDto[]>([]);
    const [professions, setProfessions] = useState<ProfessionDto[]>([]);
    const [levels, setLevels] = useState<ProfessionLevel[]>([]);
    const [filteredLevels, setFilteredLevels] = useState<ProfessionLevel[]>([]);

    // Pagination
    const [paging, setPaging] = useState<PagingDto>({
        pageNumber: 1,
        maxPageSize: null,
        pageSize: 10,
    });

    // Loading states
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [isExporting, setIsExporting] = useState<boolean>(false);

    const { register, handleSubmit, reset, setValue } = useForm();

    // Memoized options for multi-selects to prevent re-renders
    const statusOptions = useMemo(() =>
            Object.entries(ProgramStatus)
                .filter((p) => !isNaN(Number(p[0])) && Number(p[0]) <= 13)
                .map((p) => ({
                    label: String(p[1]),
                    value: String(p[0]),
                })),
        []);

    const degreeTypeOptions = useMemo(() =>
            Object.entries(DegreeTypes)
                .filter((d) => !isNaN(Number(d[0])))
                .map((d) => ({
                    label: String(d[1]),
                    value: String(d[0]),
                })),
        []);

    const stateOptions = useMemo(() =>
            States?.map((s) => ({
                label: s.name,
                value: s.abbreviation,
            })) || [],
        []);

    const auspiceOptions = useMemo(() =>
            auspices?.map((a) => ({
                label: a.title,
                value: String(a.auspiceID),
            })) || [],
        [auspices]);

    // Load initial data
    useEffect(() => {
        let isMounted = true;

        const loadInitialData = async () => {
            try {
                const [professionsData, levelsData, institutionsData, auspicesData, typesData] = await Promise.all([
                    professionService.getProfessions(),
                    professionService.getAllLevels(),
                    institutionService.getInstitutions(),
                    institutionService.getAuspice(),
                    institutionService.getTypes(),
                ]);

                if (isMounted) {
                    setProfessions(
                        professionsData
                            .filter((p) => p.professionId === 32 || !p.isDeleted)
                            .sort((a, b) => a.title.localeCompare(b.title))
                    );
                    setLevels(levelsData);
                    setFilteredLevels(levelsData);
                    setInstitutions(institutionsData);
                    setAuspices(
                        auspicesData
                            .filter((r) => r.isActive === true)
                            .sort((a, b) => a.title.localeCompare(b.title))
                    );
                    setInstitutionTypes(
                        typesData
                            .filter((r) => r.isActive === true)
                            .sort((a, b) => a.title.localeCompare(b.title))
                    );
                }
            } catch (error) {
                if (isMounted) {
                    toast.error('Error loading initial data');
                    console.error('Failed to load initial data:', error);
                }
            }
        };

        loadInitialData();

        return () => {
            isMounted = false;
        };
    }, []);

    // Search for accredited programs
    useEffect(() => {
        let isMounted = true;
        let toastId: React.ReactText | null = null;

        const getData = async () => {
            setIsSearching(true);
            toastId = toast.info('Searching...', { autoClose: false });

            try {
                const response = await reportService.getAccreditedProgramsNew(
                    paging,
                    professionId || undefined,
                    selectedStatus?.map((s) => Number(s.value)),
                    institutionId || undefined,
                    institutionTypeId || undefined,
                    selectedDegreeType?.map((d) => Number(d.value)),
                    selectedAuspices?.map((a) => Number(a.value)),
                    city || undefined,
                    selectedStates?.map((s) => s.value), // States remain as strings
                    distance,
                    professionLevelId || undefined,
                );

                if (isMounted) {
                    setAccreditedPrograms(response.records || []);
                    setTotal(response.total || 0);
                    if (toastId) {
                        toast.update(toastId, {
                            render: 'Search completed',
                            type: 'success',
                            autoClose: 1000,
                            closeOnClick: true,
                            draggable: true,
                        });
                    }
                }
            } catch (error) {
                if (isMounted) {
                    if (toastId) {
                        toast.update(toastId, {
                            render: 'Failed to get accredited programs',
                            type: 'error',
                            autoClose: 3000,
                            closeOnClick: true,
                            draggable: true,
                        });
                    }
                    console.error('Search failed:', error);
                }
            } finally {
                if (isMounted) {
                    setIsSearching(false);
                }
            }
        };

        getData();

        return () => {
            isMounted = false;
            if (toastId) {
                toast.dismiss(toastId);
            }
        };
    }, [paging, professionId, selectedStatus, institutionId, institutionTypeId,
        selectedDegreeType, selectedAuspices, city, selectedStates, distance, professionLevelId]);

    // Filter levels based on selected profession
    const filterLevels = useCallback((professionId: number) => {
        if (professionId > 0) {
            const filtered = levels?.filter((l) => l.professionId === professionId) || [];
            setFilteredLevels(filtered);
            // Clear profession level if it's not in the filtered list
            if (filtered.length === 0 || !filtered.some(l => l.id === professionLevelId)) {
                setProfessionLevelId(0);
                setValue('professionLevelId', '');
            }
        } else {
            setFilteredLevels(levels || []);
            setProfessionLevelId(0);
            setValue('professionLevelId', '');
        }
    }, [levels, professionLevelId, setValue]);

    // Handle profession change
    const handleProfessionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newProfessionId = Number(e.target.value);
        setProfessionId(newProfessionId);
        filterLevels(newProfessionId);
    }, [filterLevels]);

    // Clear search form
    const clearSearchForm = useCallback(() => {
        setProfessionId(0);
        setProfessionLevelId(0);
        setSelectedStatus([]);
        setInstitutionId(0);
        setInstitutionTypeId(0);
        setSelectedDegreeType([]);
        setSelectedAuspices([]);
        setCity('');
        setSelectedStates([]);
        setDistance(false);
        setFilteredLevels(levels || []);
        setPaging({
            pageNumber: 1,
            maxPageSize: null,
            pageSize: 10,
        });
        reset({
            professionId: '',
            professionLevelId: '',
            institutionId: '',
            institutionTypeId: '',
            city: '',
            distance: false,
        });
    }, [levels, reset]);

    // Handle form submission (triggers search through paging update)
    const onSubmit = useCallback(() => {
        setPaging(prev => ({ ...prev, pageNumber: 1 }));
    }, []);

    // Export report
    const exportReport = useCallback(async () => {
        setIsExporting(true);
        const toastId = toast.info('Generating report, this may take a minute...', {
            autoClose: false,
        });

        try {
            const report = await reportService.exportAccreditedProgramReportNew(
                professionId || undefined,
                selectedStatus?.map((s) => Number(s.value)),
                institutionId || undefined,
                institutionTypeId || undefined,
                selectedDegreeType?.map((d) => Number(d.value)),
                selectedAuspices?.map((a) => Number(a.value)),
                city || undefined,
                selectedStates?.map((s) => s.value), // States remain as strings
                distance,
                professionLevelId || undefined,
            );

            if (report) {
                const url = window.URL.createObjectURL(report);
                const a = document.createElement('a');
                a.href = url;
                a.download = `AccreditedProgramReport_${moment().format('YYYY-MM-DD')}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                toast.update(toastId, {
                    render: 'Report generated successfully',
                    type: 'success',
                    autoClose: 2500,
                    closeOnClick: true,
                    draggable: true,
                });
            }
        } catch (error) {
            toast.update(toastId, {
                render: 'Unable to generate report',
                type: 'error',
                autoClose: 3000,
                closeOnClick: true,
                draggable: true,
            });
            console.error('Export failed:', error);
        } finally {
            setIsExporting(false);
        }
    }, [professionId, selectedStatus, institutionId, institutionTypeId,
        selectedDegreeType, selectedAuspices, city, selectedStates, distance, professionLevelId]);

    // Pagination handlers
    const handlePreviousPage = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setPaging(prev => ({ ...prev, pageNumber: Math.max(1, prev.pageNumber - 1) }));
    }, []);

    const handleNextPage = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        const maxPage = Math.ceil(total / (paging.pageSize || 10));
        setPaging(prev => ({ ...prev, pageNumber: Math.min(maxPage, prev.pageNumber + 1) }));
    }, [total, paging.pageSize]);

    // Calculate if there are more pages
    const hasNextPage = useMemo(() =>
            total > paging.pageNumber * (paging.pageSize || 10),
        [total, paging.pageNumber, paging.pageSize]);

    return (
        <Card>
            <CardBody>
                <Row>
                    <Col>
                        <div className="mb-3">
                            <h3>Accredited Programs Report</h3>
                        </div>
                        <Form noValidate onSubmit={handleSubmit(onSubmit)}>
                            <div className="d-flex align-items-center flex-wrap" style={{ gap: '1rem' }}>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="professionId" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Profession
                                    </Label>
                                    <Input
                                        type="select"
                                        id="professionId"
                                        {...register('professionId')}
                                        style={{ width: '300px' }}
                                        onChange={handleProfessionChange}
                                    >
                                        <option value="">Select...</option>
                                        {professions?.map((p) => (
                                            <option key={p.professionId} value={p.professionId}>
                                                {p.title}
                                            </option>
                                        ))}
                                    </Input>
                                </div>

                                {filteredLevels && filteredLevels.length > 0 && (
                                    <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                        <Label for="professionLevelId" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                            Profession Level
                                        </Label>
                                        <Input
                                            type="select"
                                            id="professionLevelId"
                                            {...register('professionLevelId')}
                                            style={{ width: '300px' }}
                                            onChange={(e) => setProfessionLevelId(Number(e.target.value) || 0)}
                                        >
                                            <option value="">Select...</option>
                                            {filteredLevels?.map((p) => (
                                                <option key={p.id} value={p.id}>
                                                    {p.title}
                                                </option>
                                            ))}
                                        </Input>
                                    </div>
                                )}

                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="status" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Status
                                    </Label>
                                    <StyledMultiSelect
                                        options={statusOptions}
                                        labelledBy="Status"
                                        disableSearch={true}
                                        hasSelectAll={false}
                                        value={selectedStatus}
                                        onChange={setSelectedStatus}
                                    />
                                </div>

                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="degreeTypes" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Degree Types
                                    </Label>
                                    <StyledMultiSelect
                                        options={degreeTypeOptions}
                                        disableSearch={true}
                                        hasSelectAll={false}
                                        value={selectedDegreeType}
                                        onChange={setSelectedDegreeType}
                                        labelledBy="Degree Types"
                                    />
                                </div>

                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="institutionId" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Institution
                                    </Label>
                                    <Input
                                        type="select"
                                        id="institutionId"
                                        {...register('institutionId')}
                                        style={{ width: '300px' }}
                                        onChange={(e) => setInstitutionId(Number(e.target.value) || 0)}
                                    >
                                        <option value="">Select...</option>
                                        {institutions?.map((i) => (
                                            <option key={i.institutionId} value={i.institutionId}>
                                                {i.title}
                                            </option>
                                        ))}
                                    </Input>
                                </div>

                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="institutionTypeId" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Institution Type
                                    </Label>
                                    <Input
                                        type="select"
                                        id="institutionTypeId"
                                        {...register('institutionTypeId')}
                                        style={{ width: '300px' }}
                                        onChange={(e) => setInstitutionTypeId(Number(e.target.value) || 0)}
                                    >
                                        <option value="">Select...</option>
                                        {institutionTypes?.map((t) => (
                                            <option key={t.institutionTypeId} value={t.institutionTypeId}>
                                                {t.title}
                                            </option>
                                        ))}
                                    </Input>
                                </div>

                                {auspices && auspices.length > 0 && (
                                    <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                        <Label for="auspice" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                            Auspice
                                        </Label>
                                        <StyledMultiSelect
                                            options={auspiceOptions}
                                            disableSearch={true}
                                            hasSelectAll={false}
                                            value={selectedAuspices}
                                            onChange={setSelectedAuspices}
                                            labelledBy="Auspice"
                                        />
                                    </div>
                                )}

                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="city" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        City
                                    </Label>
                                    <Input
                                        type="text"
                                        id="city"
                                        {...register('city')}
                                        placeholder="City"
                                        style={{ width: '300px' }}
                                        onChange={(e) => setCity(e.target.value)}
                                    />
                                </div>

                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="state" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        State
                                    </Label>
                                    <StyledMultiSelect
                                        options={stateOptions}
                                        disableSearch={true}
                                        hasSelectAll={false}
                                        value={selectedStates}
                                        onChange={setSelectedStates}
                                        labelledBy="State"
                                    />
                                </div>

                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <div className="custom-control custom-checkbox">
                                        <Input
                                            type="checkbox"
                                            id="distance"
                                            {...register('distance')}
                                            checked={distance}
                                            onChange={(e) => setDistance(e.target.checked)}
                                            className="custom-control-input"
                                        />
                                        <Label for="distance" className="custom-control-label">
                                            Distance
                                        </Label>
                                    </div>
                                </div>
                            </div>

                            {/* Button row within the form */}
                            <Row className="mt-3">
                                <Col className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                        <Button
                                            type="submit"
                                            color="primary"
                                            disabled={isSearching}
                                        >
                                            {isSearching ? 'Searching...' : 'Search'}
                                        </Button>
                                        <Button
                                            type="button"
                                            color="link"
                                            onClick={clearSearchForm}
                                            disabled={isSearching}
                                        >
                                            Clear
                                        </Button>
                                    </div>
                                    <Button
                                        type="button"
                                        color="info"
                                        onClick={exportReport}
                                        disabled={isExporting || accreditedPrograms.length === 0}
                                    >
                                        {isExporting ? 'Exporting...' : 'Export Report'}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </Col>
                </Row>

                <div className="mt-3">
                    <h4>Accredited Programs {isSearching && <small className="text-muted">(Loading...)</small>}</h4>
                </div>
            </CardBody>

            <Table responsive hover>
                <thead>
                <tr>
                    <th>Institution ID</th>
                    <th>Institution Name</th>
                    <th>Institution City</th>
                    <th>Institution State</th>
                    <th>Institution Type</th>
                    <th>Institutional Accreditor</th>
                    <th>Program ID</th>
                    <th>Profession Title</th>
                    <th>Level/Concentration</th>
                    <th>Degree(s)</th>
                    <th>Accreditation Status</th>
                    <th>Date First Accredited</th>
                    <th>Program City</th>
                    <th>Program State</th>
                </tr>
                </thead>
                <tbody>
                {accreditedPrograms && accreditedPrograms.length > 0 ? (
                    accreditedPrograms.map((program, i) => (
                        <tr key={program.programId || `program-${i}`}>
                            <td>{program.institutionId}</td>
                            <td>{program.institutionName}</td>
                            <td>{program.institutionCity}</td>
                            <td>{program.institutionState}</td>
                            <td>{program.institutionType}</td>
                            <td>{program.institutionalAccreditor || '-'}</td>
                            <td>{program.programId}</td>
                            <td>{program.professionTitle}</td>
                            <td>
                                {program.professionLevelTitle || program.concentrationTitle || '-'}
                            </td>
                            <td>{program.degree}</td>
                            <td>{program.accreditationStatusTitle}</td>
                            <td>
                                {program.dateFirstAccredited
                                    ? moment(program.dateFirstAccredited).format('MM/DD/YYYY')
                                    : '-'}
                            </td>
                            <td>{program.city}</td>
                            <td>{program.state}</td>
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan={14} className="text-center">
                            {isSearching ? 'Loading...' : 'No Results'}
                        </td>
                    </tr>
                )}
                </tbody>
            </Table>

            <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                    <h5>Total: {total.toLocaleString()}</h5>
                    <Pagination size="sm" aria-label="Accredited Programs Navigation">
                        <PaginationItem disabled={paging.pageNumber === 1 || isSearching}>
                            <PaginationLink
                                previous
                                href="#"
                                onClick={handlePreviousPage}
                            />
                        </PaginationItem>
                        <PaginationItem active>
                            <PaginationLink href="#">
                                Page {paging.pageNumber}
                            </PaginationLink>
                        </PaginationItem>
                        <PaginationItem disabled={!hasNextPage || isSearching}>
                            <PaginationLink
                                next
                                href="#"
                                onClick={handleNextPage}
                            />
                        </PaginationItem>
                    </Pagination>
                </div>
            </CardBody>
        </Card>
    );
};

export default AccreditedProgramsReportNew;